import styled from '@emotion/styled';
import React, { useState } from 'react';
import { BsImage as Image } from 'react-icons/bs';

import IconLogo from './B_Grey.svg';
import GreyLogo from './BAY_Grey.svg';
import PinkLogo from './BAY_Pink.svg';
import PoweredByLogo from './BAY_PoweredBy_Grey.svg';
import ClientLogo from './ClientBAY.svg';
import DesignLogo from './DesignBAY.svg';
import FinancialLogo from './FinancialBAY.svg';
import SocialLogo from './SocialBAY.svg';
import SitesLogo from './SitesBAY.svg';
import WordmarkLogo from './BackAtYou_Wordmark.svg';
import type { ProductLogoPlatform } from './constants';

interface BayLogoProps {
	/**
	 * Override the logo with your own URL
	 */
	source?: string;
	/**
	 * If you want to set a different size by adjusting the height (width is auto calculated)
	 * using a percentage or pixel amount, or a predefined setting
	 */
	size?: 'large' | 'small' | string;
	/**
	 * Which style of branding to use
	 */
	logoStyle?: 'grey' | 'powered' | 'full' | 'icon' | 'bay' | 'wordmark';
	/**
	 * Which platform to show
	 */
	platform?: ProductLogoPlatform;
	/**
	 * Alt text for the logo image. For a full product logo, pass
	 * `PRODUCT_LOGO_ALT[platform]` (exported from the package root).
	 */
	alt?: string;
}

const width = (props: Pick<BayLogoProps, 'size'>) => {
	let displayHeight = 'auto';
	let displayWidth = 'auto';

	if (props.size && props.size.length) {
		if (props.size === 'large') {
			displayHeight = 'auto';
			displayWidth = '100%';
		} else if (props.size === 'small') {
			displayHeight = 'auto';
			displayWidth = '15%';
		} else {
			displayHeight = props.size;
		}
	}

	return `
			width: ${displayWidth};
			height: ${displayHeight};
			justify-content: center;
	`;
};

const LogoImage = styled.img`
	${(props: Pick<BayLogoProps, 'size'>) => width(props)}
`;

const BayLogo = ({
	size,
	logoStyle = 'full',
	platform = 'client',
	source,
	alt = 'logo',
}: BayLogoProps) => {
	const [imageError, setImageError] = useState(false);

	const checkSource = (sourceString?: string) => {
		if (sourceString && sourceString.length > 0) {
			return true;
		}
		return false;
	};

	const checkPlatform = (platformArg?: BayLogoProps['platform']) => {
		let newSource = '';

		if (platformArg && platformArg.length > 0 && logoStyle === 'full') {
			switch (platformArg) {
			case 'client':
				newSource = ClientLogo;
				break;
			case 'financial':
				newSource = FinancialLogo;
				break;
			case 'social':
				newSource = SocialLogo;
				break;
			case 'design':
				newSource = DesignLogo;
				break;
			case 'sites':
				newSource = SitesLogo;
				break;
			default:
				break;
			}
		} else if (logoStyle !== 'full') {
			switch (logoStyle) {
			case 'bay':
				newSource = PinkLogo;
				break;
			case 'grey':
				newSource = GreyLogo;
				break;
			case 'icon':
				newSource = IconLogo;
				break;
			case 'powered':
				newSource = PoweredByLogo;
				break;
			case 'wordmark':
				newSource = WordmarkLogo;
				break;

			default:
				break;
			}
		}

		return newSource;
	};

	const setImage = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
		event.currentTarget.onerror = null;
		setImageError(true);
		//event.target.src=`/global/images/empty-state/${cardImage ? 'card-image' : 'image'}.svg`
	};

	return !imageError ? (
		<LogoImage
			alt={alt}
			className="App-logo"
			size={size}
			src={checkSource(source) ? source : checkPlatform(platform)}
			onError={(e) => setImage(e)}
		/>
	) : (
		// Stands in for the <img>, so it keeps the alt as its accessible name; an
		// empty alt stays decorative, as it is on the <img>.
		<span
			className="logo-image"
			role={alt ? 'img' : undefined}
			aria-label={alt || undefined}
		>
			<Image aria-hidden="true" />
		</span>
	);
};

export default BayLogo;
